import { ImageProps as NextImageProps } from 'next/image';
import clsx from 'clsx';
import { DynamicImageLoader } from '~/shared/components/DynamicImageLoader';
import { useCanHover } from '~/shared/hooks/useCanHover';
import styles from './ImageSwitcher.module.scss';
import { mq } from '~/lib';
import { useMemo } from 'react';

type ProductImage = {
    src: string;
    width?: number;
    height?: number;
    alt?: string;
};

type ImageSwitcherProps = {
    mainImage: ProductImage;
    hoverImage?: ProductImage;
    priority?: boolean;
    imageLoading?: NextImageProps['loading'];
};

export const ImageSwitcher = ({
    mainImage,
    hoverImage,
    priority,
    imageLoading,
}: ImageSwitcherProps) => {
    const canHover = useCanHover();
    // we do not want to load in the second image on e.g. Ipads that cannot utilize the hover animation
    const showHoverImage = hoverImage && canHover;

    const sharedProps = useMemo(
        () => ({
            fill: true,
            loading: imageLoading,
            style: { objectFit: 'cover' as const },
            sizes: `${mq(0, 'md', false)} 50vw, ${mq(0, 'lg', false)} 34vw, 25vw`,
        }),
        [imageLoading],
    );

    return (
        <div className={clsx(styles.crossFade, { [styles.hasHoverImage]: showHoverImage })}>
            <DynamicImageLoader
                src={mainImage.src}
                alt={mainImage.alt ?? ''}
                priority={priority}
                {...sharedProps}
            />

            {showHoverImage && (
                <DynamicImageLoader
                    src={hoverImage.src}
                    alt={hoverImage.alt ?? ''}
                    priority={false}
                    {...sharedProps}
                />
            )}
        </div>
    );
};
